/**
 * Department Model - Encapsulates department data
 */
class DepartmentModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  get dept_code() {
    return this._serverData?.dept_code ?? '';
  }

  get name() {
    return this._serverData?.name ?? '';
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: any) {
    return new DepartmentModel(serverData);
  }
}

/**
 * Tax Model - Encapsulates tax data
 */
class TaxModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  get tax_code() {
    return this._serverData?.tax_code ?? '';
  }

  get name() {
    return this._serverData?.name ?? '';
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: any) {
    return new TaxModel(serverData);
  }
}

/**
 * Promotion Model - Encapsulates promotion data
 */
class PromotionModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  get promotion_id() {
    return this._serverData?.promotion_id ?? '';
  }

  get promotion_type() {
    return this._serverData?.promotion_type ?? '';
  }

  get program_name() {
    return this._serverData?.program_name ?? null;
  }

  get promotion_reason() {
    return this._serverData?.promotion_reason ?? '';
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: any) {
    return new PromotionModel(serverData);
  }
}

/**
 * Linked Item Model - Encapsulates linked item data
 */
class LinkedItemModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  get pos_code() {
    return this._serverData?.pos_code ?? '';
  }

  get description() {
    return this._serverData?.description ?? '';
  }

  get regular_price() {
    return this._serverData?.regular_price ?? 0;
  }

  getRawData() {
    return this._serverData;
  }

  static fromServerData(serverData: any) {
    return new LinkedItemModel(serverData);
  }
}

/**
 * Pricebook Metadata Model - Encapsulates pricebook metadata and provides clean interface for UI components
 * This model handles data transformation and provides access to departments, taxes, and promotions
 */
class PricebookMetadataModel {
  private _departments: DepartmentModel[];
  private _taxes: TaxModel[];
  private _promotions: PromotionModel[];
  private _linked_items: LinkedItemModel[];

  constructor(serverData: any = null) {
    if (serverData) {
      this._departments = (serverData.departments || []).map((dept: any) => DepartmentModel.fromServerData(dept));
      this._taxes = (serverData.taxes || []).map((tax: any) => TaxModel.fromServerData(tax));
      this._promotions = (serverData.promotions || []).map((promo: any) => PromotionModel.fromServerData(promo));
      this._linked_items = (serverData.linked_items || []).map((linkedItem: any) => LinkedItemModel.fromServerData(linkedItem));
    } else {
      this._departments = [];
      this._taxes = [];
      this._promotions = [];
      this._linked_items = [];
    }
  }

  // ===== COLLECTIONS =====

  get departments() {
    return this._departments;
  }

  get taxes() {
    return this._taxes;
  }

  get promotions() {
    return this._promotions;
  }

  get linked_items() {
    return this._linked_items;
  }

  // ===== UTILITY METHODS =====

  getRawData() {
    return {
      departments: this._departments.map(dept => dept.getRawData()),
      taxes: this._taxes.map(tax => tax.getRawData()),
      promotions: this._promotions.map(promo => promo.getRawData()),
      linked_items: this._linked_items.map(linkedItem => linkedItem.getRawData()),
    };
  }

  // ===== STATIC METHODS =====

  static fromServerData(serverData: any) {
    return new PricebookMetadataModel(serverData);
  }

  static createEmpty() {
    return new PricebookMetadataModel();
  }
}

export default PricebookMetadataModel;
export { DepartmentModel, TaxModel, PromotionModel, LinkedItemModel };

